import { useEffect, useState } from 'react';
import { db } from './schema';

/**
 * Object URLs leak: `createObjectURL` without a matching `revokeObjectURL`
 * makes memory climb until the app is unusable. This hook is the only place
 * in the app that calls either. Nothing else may.
 *
 * The cache is module-level and refcounted, so the same image shown in the
 * rail, a cover card and the panel at once costs one URL, and it is revoked
 * when the last of them unmounts.
 */

type Variant = 'thumb' | 'full';

type Entry = {
  refs: number;
  url?: string;
  /** Set when the last ref goes; an in-flight load revokes rather than publishes. */
  dead: boolean;
  loading: Promise<string | undefined>;
};

const cache = new Map<string, Entry>();

/** `thumb` for boards and cards, `full` only where the pixels are actually seen. */
export function useImageUrl(imageId: string | undefined, variant: Variant = 'thumb'): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (imageId === undefined) {
      setUrl(undefined);
      return;
    }

    const key = `${variant}:${imageId}`;
    const entry = acquire(key, imageId, variant);
    let live = true;

    if (entry.url !== undefined) {
      setUrl(entry.url);
    } else {
      void entry.loading.then((loaded) => {
        if (live) setUrl(loaded);
      });
    }

    return () => {
      live = false;
      // Clear first: once released the URL may be revoked, and a revoked URL
      // in a live <img> renders as a broken image.
      setUrl(undefined);
      release(key);
    };
  }, [imageId, variant]);

  return url;
}

/**
 * The same discipline for a blob that isn't in the database yet — a file the
 * user has just picked and not saved. Separate from the cache above because
 * there is no id to key on; the blob's own lifetime is the URL's.
 */
export function useBlobUrl(blob: Blob | undefined): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (blob === undefined) {
      setUrl(undefined);
      return;
    }

    const created = URL.createObjectURL(blob);
    setUrl(created);
    return () => {
      setUrl(undefined);
      URL.revokeObjectURL(created);
    };
  }, [blob]);

  return url;
}

function acquire(key: string, imageId: string, variant: Variant): Entry {
  const existing = cache.get(key);
  if (existing !== undefined) {
    existing.refs += 1;
    return existing;
  }

  const entry: Entry = { refs: 1, dead: false, loading: Promise.resolve(undefined) };
  entry.loading = load(entry, imageId, variant);
  cache.set(key, entry);
  return entry;
}

async function load(entry: Entry, imageId: string, variant: Variant): Promise<string | undefined> {
  const record = await db.images.get(imageId);
  if (record === undefined) return undefined;

  const url = URL.createObjectURL(variant === 'thumb' ? record.thumb : record.blob);
  if (entry.dead) {
    URL.revokeObjectURL(url);
    return undefined;
  }

  entry.url = url;
  return url;
}

function release(key: string): void {
  const entry = cache.get(key);
  if (entry === undefined) return;

  entry.refs -= 1;
  if (entry.refs > 0) return;

  entry.dead = true;
  cache.delete(key);
  if (entry.url !== undefined) URL.revokeObjectURL(entry.url);
}
